import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Discord as DiscordIcon } from 'react-bootstrap-icons';

import { ProfileStateContext } from '../../context/ProfileDataContext';
import { ComponentTypes, ICohort } from '../../utils/interfaces/DbInterfaces';
import { ICompDefault, MONTH } from '../../utils/interfaces/TypesInterfaces';
import AchievmentsBar from '../AchievmentsBar';
import { IAchievementItem } from '../AchievmentsBar/AchievementItem/AchievementItem';
import LeagueInfo from '../LeagueInfo/LeagueInfo';
import ProfilePicture from '../ProfilePicture';

import styles from './User.module.scss';

export interface IAchivementsDisplayed {
  certificates: boolean;
  achiements: boolean;
}
export interface IUser extends ICompDefault {
  openContactMeModal: (e?: React.BaseSyntheticEvent) => void;
  clickedAchievementItem: (type: ComponentTypes) => void;
  cohort?: ICohort;
}

const User = ({ className, clickedAchievementItem, openContactMeModal, cohort }: IUser) => {
  const { profileData, certificatesData, activityData, discordData, socialMediaData } = useContext(ProfileStateContext);
  const nick = useMemo(() => profileData.id, [profileData.id]);

  // Check if user exists in cohort data
  const userExistsInCohort = useMemo(() => cohort?.users?.some((user) => user.userId === profileData.userId) || false, [cohort, profileData.userId]);

  // Use only the top-level avatar property or fallback to base lynx if user has cohort
  const mainAvatar = useMemo(() => {
    if ((profileData as any).avatar) {
      return (profileData as any).avatar;
    }
    // Only provide default lynx avatar if user exists in cohort
    if (userExistsInCohort) {
      return { version: 1, base: 'lynx-base', layers: { base: 'lynx-base' } };
    }
    return undefined;
  }, [profileData, userExistsInCohort]);

  // Get mood from avatar layers
  const currentMood = useMemo(() => {
    if (mainAvatar?.layers?.mood) {
      return mainAvatar.layers.mood;
    }

    return undefined;
  }, [mainAvatar]);

  const displayName = useMemo(() => (profileData as any).nickname || profileData.id, [profileData]);

  // Get subscription level display text
  const subscriptionDisplayText = useMemo(() => {
    if (profileData.subscriptionLevel) {
      // Capitalize the first letter of subscription level
      return profileData.subscriptionLevel.charAt(0).toUpperCase() + profileData.subscriptionLevel.slice(1);
    }
    return 'Free'; // Default fallback
  }, [profileData.subscriptionLevel]);
  const [displayAchievements, setDisplayAchievements] = useState(false);

  const clickOnAchievementItem = useCallback(
    (event: React.BaseSyntheticEvent, type: ComponentTypes) => {
      event.preventDefault();
      event.stopPropagation();
      clickedAchievementItem(type);
    },
    [clickedAchievementItem],
  );

  const achievementData = useMemo<IAchievementItem[]>(() => {
    const myData: IAchievementItem[] = [];
    let achievementDisplay = false;

    /* if (certificatesData && certificatesData.visible && certificatesData.certificates.length > 0) {
      achievementDisplay = true;

      myData.push({
        value: certificatesData.certificates.length,
        text: 'Certificates',
        icon: <W3BadgeIcon className={styles.icon} />,
        type: ComponentTypes.Certificates,
        onClick: clickOnAchievementItem,
      });
    } */

    /* if (activityData && activityData.visible) {
      achievementDisplay = true;
      myData.push({
        value: activityData.stars,
        text: 'Activity Stars',
        icon: <StarIcon className={styles.icon} />,
        type: ComponentTypes.Activity,
        onClick: clickOnAchievementItem,
      });
    } */

    if (discordData && discordData.visible) {
      achievementDisplay = true;
      myData.push({
        text: discordData.role,
        icon: <DiscordIcon className={styles.icon} />,
        type: ComponentTypes.Discord,
        onClick: clickOnAchievementItem,
      });
    }

    setDisplayAchievements(achievementDisplay);

    return myData;
  }, [clickOnAchievementItem, discordData]);

  const [showSocialMedia, setShowSocialMedia] = useState(false);
  const [disableContactMe, setDisableContactMe] = useState(false);

  const memberSince = useMemo(() => {
    if (profileData.createdAt) {
      const myDate = new Date(profileData.createdAt * 1000);

      return `W3Schooler since ${MONTH[myDate.getMonth()]}, ${myDate.getFullYear()}`;
    }

    return '';
  }, [profileData.createdAt]);

  const renderSubscriptionBadge = () => {
    if (!userExistsInCohort) {
      return null;
    }

    if (subscriptionDisplayText === 'Plus') {
      return (
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip id='plus-tooltip'>Plus Member – Enjoys extra perks, faster progress, and many more features</Tooltip>}
        >
          <span
            className={styles.subscription_badge}
            style={{ cursor: 'pointer' }}
            onClick={() => window.open('https://order.w3schools.com/plans/index.html', '_blank', 'noopener,noreferrer')}
            role='button'
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                window.open('https://order.w3schools.com/plans/index.html', '_blank', 'noopener,noreferrer');
              }
            }}
            aria-label='Learn more about Plus membership'
          >
            {subscriptionDisplayText}
          </span>
        </OverlayTrigger>
      );
    }

    if (subscriptionDisplayText === 'Hero') {
      return <span className={styles.subscription_badge_hero}>{subscriptionDisplayText}</span>;
    }

    return <span className={styles.subscription_badge}>{subscriptionDisplayText}</span>;
  };

  // check if Contact Me button should be disabled
  useEffect(() => {
    let visible = profileData.basic.email.visible || profileData.basic.phone.visible || profileData.basic.website.visible;

    if (!visible) {
      socialMediaData.forEach((item) => {
        if (item.visible) {
          visible = true;
        }
      });
    }

    setDisableContactMe(!visible);
  }, [profileData.basic.email.visible, profileData.basic.phone.visible, profileData.basic.website.visible, socialMediaData]);

  // hook to check if any social media elements should be displayed
  useEffect(() => {
    if (profileData.socialMedias.length > 0) {
      const displayIndex = profileData.socialMedias.findIndex((item) => item.visible);
      setShowSocialMedia(displayIndex > -1);
    }
  }, [profileData.socialMedias]);

  return (
    <div className={`${styles.user}${className ? ` ${className}` : ''}`}>
      <div className={styles.profile_section}>
        <div className={styles.main_content}>
          <div className={styles.picture_wrapper}>
            <ProfilePicture
              firstName={profileData.basic.firstName}
              lastName={profileData.basic.lastName}
              nick={nick}
              className={`${styles.public_picture}`}
              srcURL={userExistsInCohort ? undefined : profileData.basic.profilePicture}
              fadeIn
              mood={currentMood}
              avatar={mainAvatar}
            />
          </div>

          <div className={styles.profile_info}>
            <div className={styles.header_row}>
              <div className={styles.name_and_badge}>
                <div className={styles.name}>{displayName}</div>
                {/* Subscription badge (Plus, Hero, or default) */}
                {renderSubscriptionBadge()}
              </div>
              {displayAchievements && <AchievmentsBar data={achievementData} />}
            </div>

            <div className={styles.member_info}>
              <div className={styles.nick_and_member}>{memberSince}</div>
            </div>
          </div>
        </div>
        <div className={styles.side_content}>
          {/* Only show LeagueInfo if user exists in cohort data */}
          {userExistsInCohort && (
            <LeagueInfo
              cohort={cohort}
              userRank={(() => {
                if (!cohort || !cohort.users) return undefined;
                const sortedUsers = [...cohort.users].sort((a, b) => {
                  const xpDiff = (b.weeklyXP || 0) - (a.weeklyXP || 0);
                  if (xpDiff !== 0) return xpDiff;
                  return a.nickname.localeCompare(b.nickname);
                });
                const currentNickname = (profileData as any).nickname || '';
                const idx = sortedUsers.findIndex((u) => u.nickname && u.nickname.trim().toLowerCase() === currentNickname.trim().toLowerCase());
                return idx >= 0 ? idx + 1 : undefined;
              })()}
            />
          )}
        </div>
      </div>

      {/* {showSocialMedia && <Social openContactMe={() => openContactMeModal()} />}

      <div className={`${styles.contact_wrapper}${showSocialMedia ? ` ${styles.move_up}` : ''}`}>
        <Button
          disabled={disableContactMe}
          className={`${disableContactMe ? styles.disabled : ''}`}
          text='Contact Me!'
          variant='primary'
          onClick={openContactMeModal}
        />
      </div> */}
    </div>
  );
};

User.defaultProps = {
  cohort: undefined,
};

export default User;
